import React from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { useDropdownMenu } from './DropdownMenu'
import Toggle from '@/components/ui/toggle'
import CheckControl from '@/components/ui/CheckControl'
import RadioControl from '@/components/ui/RadioControl'
import './MenuItem.scss'

/** @deprecated Use MenuItem default styles instead of overriding with navbar row classes. */
export const MENU_ITEM_BASE_CLASS = 'menu-item'

const TRAILING_CONTROLS = ['none', 'toggle', 'checkbox', 'radio']

/**
 * Dropdown menu row (molecule).
 *
 * - `toggle` is trailing (right); optional leading `icon` and optional `trailingIcon` (chevron, etc.).
 * - `checkbox` and `radio` are leading (left). A leading `icon` may be shown next to them
 *   (order: control → icon → label). `trailingIcon` is hidden for these controls.
 */
const MenuItem = React.forwardRef(({
  icon,
  /** When false, hides leading icon even if `icon` is set. Default: show when `icon` is provided. */
  showLeadingIcon,
  text,
  trailingControl = 'none',
  /** @deprecated Use `trailingControl`. `icon` value maps to trailing icon only. */
  trailingType,
  trailingIcon,
  active = false,
  disabled = false,
  onClick,
  onToggle,
  action,
  url,
  target = '',
  rel,
  hidden = false,
  className = '',
  closeMenuOnClick = true,
  hoverable = true,
}, ref) => {
  const dropdownMenu = useDropdownMenu()
  const handleAction = action ?? onClick
  const isLink = Boolean(url)
  const isExternalLink = isLink && /^https?:\/\//.test(url)

  let resolvedTrailingControl = trailingControl
  if (trailingType) {
    if (trailingType === 'icon') {
      resolvedTrailingControl = 'none'
    } else if (TRAILING_CONTROLS.includes(trailingType)) {
      resolvedTrailingControl = trailingType
    }
  }

  if (!TRAILING_CONTROLS.includes(resolvedTrailingControl)) {
    resolvedTrailingControl = 'none'
  }

  // checkbox and radio are leading (left); toggle is trailing (right).
  const isLeadingControl =
    resolvedTrailingControl === 'checkbox' || resolvedTrailingControl === 'radio'

  const canShowTrailingIcon =
    Boolean(trailingIcon) &&
    !isLeadingControl &&
    (resolvedTrailingControl === 'none' || resolvedTrailingControl === 'toggle')

  // Leading icon can sit next to a checkbox/radio (control → icon → label).
  const canShowLeadingIcon = showLeadingIcon !== false && Boolean(icon)

  const classList = classNames(
    'menu-item',
    {
      hidden,
      'menu-item--static': !hoverable,
      'menu-item--disabled': disabled,
    },
    className,
  )

  const closeMenu = () => {
    if (closeMenuOnClick && dropdownMenu?.closeMenu) {
      dropdownMenu.closeMenu()
    }
  }

  const handleRowClick = e => {
    if (disabled || hidden) return

    if (
      resolvedTrailingControl === 'toggle' ||
      resolvedTrailingControl === 'checkbox' ||
      resolvedTrailingControl === 'radio'
    ) {
      onToggle?.(!active)
    }

    handleAction?.(e)

    if (!isLink) {
      closeMenu()
    }
  }

  const handleLinkClick = () => {
    if (disabled || hidden) return
    handleAction?.()
    closeMenu()
  }

  const renderLeadingControl = () => {
    if (!isLeadingControl) return null

    return (
      <span className="menu-item__leading-control">
        {resolvedTrailingControl === 'checkbox' && (
          <CheckControl active={active} disabled={disabled} />
        )}
        {resolvedTrailingControl === 'radio' && <RadioControl active={active} disabled={disabled} />}
      </span>
    )
  }

  const renderTrailing = () => {
    const hasToggle = resolvedTrailingControl === 'toggle'
    if (!hasToggle && !canShowTrailingIcon) return null

    return (
      <span className="menu-item__trailing">
        {hasToggle && (
          <span className="menu-item__trailing-control">
            <Toggle active={active} disabled={disabled} onToggle={() => handleAction?.()} />
          </span>
        )}
        {canShowTrailingIcon && <span className="menu-item__trailing-icon">{trailingIcon}</span>}
      </span>
    )
  }

  const content = (
    <>
      {renderLeadingControl()}
      {canShowLeadingIcon ? <span className="menu-item__leading-icon">{icon}</span> : null}
      <span className="menu-item__label">{text}</span>
      {renderTrailing()}
    </>
  )

  if (isLink) {
    if (isExternalLink) {
      const relAttr = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)
      return (
        <a
          ref={ref}
          href={url}
          className={classList}
          target={target}
          rel={relAttr}
          onClick={handleLinkClick}
          aria-disabled={disabled || undefined}>
          {content}
        </a>
      )
    }

    return (
      <Link
        ref={ref}
        className={classList}
        to={hidden ? '/' : url ?? '/'}
        onClick={handleLinkClick}
        aria-disabled={disabled || undefined}>
        {content}
      </Link>
    )
  }

  return (
    <div
      ref={ref}
      className={classList}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleRowClick}
      onKeyDown={e => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleRowClick(e)
        }
      }}
      aria-disabled={disabled || undefined}>
      {content}
    </div>
  )
})

MenuItem.displayName = 'MenuItem'

export default MenuItem
